<!-- Ported from the Polaris kit: src/product/shell/Topbar.vue, by hand (the porter does not take product/shell).
     Adapted for the dashboard: presentational (the kit reads its own nav store; here the frame passes the state in),
     no theme switch and no Brandkit link (D-G2), no ⌘B hint (the shortcut was removed in phase 1), and an
     #actions slot on the right for the page-wide buttons. -->
<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import {
  Menu16,
  OpenPanelFilledLeft16,
  OpenPanelLeft16,
} from '@carbon/icons-vue';
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '../ui/breadcrumb';
import { Button } from '../ui/button';
import { Tooltip, TooltipContent, TooltipTrigger } from '../ui/tooltip';

const props = defineProps({
  // Label of the active main-menu area ("Inbox", "Settings"...).
  area: { type: String, required: true },
  // Labels after the area; the last one is the current page, the ones before it show from lg up. A middle item
  // may be `{ label, to }` (a level that links back, phase 4.1).
  trail: { type: Array, default: () => [] },
  sectionOpen: { type: Boolean, default: false },
  // No section menu in this area: the section button is not drawn.
  hasSection: { type: Boolean, default: true },
  isMobile: { type: Boolean, default: false },
  mainCollapsed: { type: Boolean, default: false },
});

const emit = defineEmits(['toggleMain', 'toggleSection']);

const { t } = useI18n();

// One button, two jobs: on a phone it opens the navigation; otherwise it folds the main menu to its icon rail and back.
const mainLabel = computed(() => {
  if (props.isMobile) return t('POLARIS.TOPBAR.OPEN_NAVIGATION');
  return props.mainCollapsed
    ? t('POLARIS.TOPBAR.EXPAND_MAIN')
    : t('POLARIS.TOPBAR.COLLAPSE_MAIN');
});
const sectionLabel = computed(() =>
  props.sectionOpen
    ? t('POLARIS.TOPBAR.HIDE_SECTION')
    : t('POLARIS.TOPBAR.SHOW_SECTION')
);
const middle = computed(() => props.trail.slice(0, -1));
const page = computed(() => props.trail.at(-1) ?? props.area);
</script>

<template>
  <header
    class="flex h-14 shrink-0 items-center gap-2 border-0 border-b border-solid border-polaris-border px-4"
  >
    <!-- Both sidebars are controlled from here, side by side and always in reach: the menu icon is the main menu
         (the familiar fold-to-icons control), the panel icon is the section menu of the current area. -->
    <Tooltip :disabled="isMobile">
      <TooltipTrigger as-child>
        <Button
          variant="ghost"
          size="icon-sm"
          class="-ms-1"
          data-topbar-menu
          :aria-label="mainLabel"
          :aria-expanded="isMobile ? undefined : !mainCollapsed"
          :aria-haspopup="isMobile ? 'dialog' : undefined"
          @click="emit('toggleMain')"
        >
          <Menu16 aria-hidden="true" />
        </Button>
      </TooltipTrigger>
      <TooltipContent side="bottom">{{ mainLabel }}</TooltipContent>
    </Tooltip>
    <Tooltip v-if="!isMobile && hasSection">
      <TooltipTrigger as-child>
        <Button
          variant="ghost"
          size="icon-sm"
          aria-controls="section-navigation"
          :aria-expanded="sectionOpen"
          :aria-label="sectionLabel"
          @click="emit('toggleSection')"
        >
          <OpenPanelFilledLeft16
            v-if="sectionOpen"
            class="rtl:rotate-180"
            aria-hidden="true"
          />
          <OpenPanelLeft16 v-else class="rtl:rotate-180" aria-hidden="true" />
        </Button>
      </TooltipTrigger>
      <TooltipContent side="bottom">{{ sectionLabel }}</TooltipContent>
    </Tooltip>
    <Breadcrumb class="ms-1 min-w-0">
      <BreadcrumbList class="flex-nowrap">
        <!-- Tight room: no item goes under a legible floor (3rem, or its whole label when shorter: `basis-12` capped by
             `max-w-max`, `shrink-0`), and the room left goes to the area first (`grow-[400]`), then to the middle items
             (`grow-[20]`), then to the page, each up to its whole label. So the page (last) truncates first and no item
             folds to nothing next to its separator. The whole label is in `title`. Below lg the middle items and their
             separators hide together. -->
        <template v-if="trail.length">
          <BreadcrumbItem
            class="hidden min-w-0 max-w-max shrink-0 grow-[400] basis-12 sm:inline-flex"
          >
            <span class="truncate" :title="area">{{ area }}</span>
          </BreadcrumbItem>
          <BreadcrumbSeparator class="hidden list-none sm:list-item" />
        </template>
        <template v-for="(item, index) in middle" :key="index">
          <BreadcrumbItem
            class="hidden min-w-0 max-w-max shrink-0 grow-[20] basis-12 lg:inline-flex"
          >
            <BreadcrumbLink v-if="item.to" as-child>
              <router-link
                :to="item.to"
                class="max-w-48 truncate"
                :title="item.label"
              >
                {{ item.label }}
              </router-link>
            </BreadcrumbLink>
            <span v-else class="max-w-48 truncate" :title="item">
              {{ item }}
            </span>
          </BreadcrumbItem>
          <BreadcrumbSeparator class="hidden list-none lg:list-item" />
        </template>
        <BreadcrumbItem class="min-w-0 max-w-max shrink-0 grow basis-12">
          <BreadcrumbPage class="truncate font-medium" :title="page">
            {{ page }}
          </BreadcrumbPage>
        </BreadcrumbItem>
      </BreadcrumbList>
    </Breadcrumb>
    <div class="ms-auto flex shrink-0 items-center gap-1">
      <slot name="actions" />
    </div>
  </header>
</template>
